import {
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ChecklistIssues } from "Components/Merchant/OrbitL/ChecklistIssues";
import type { OrbitLChecklistDto } from "Infrastructure/Api/Api";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Pages.Content.Merchants.OrbitLOnboarding.Detail;
const CHECKLIST_RESOURCES = Resources.Components.Merchant.OrbitL.Checklist;

type Props = {
  checklist: OrbitLChecklistDto;
};

export const ChecklistSection = ({ checklist }: Props) => {
  const { t } = useResource();

  if (checklist.isComplete) {
    return (
      <Layout>
        <Alert severity="success">
          {t(CHECKLIST_RESOURCES.CompletionSuccess)}
        </Alert>
      </Layout>
    );
  }

  const items = [
    {
      label: t(CHECKLIST_RESOURCES.Items.BasicInfo),
      item: checklist.basicInfo,
    },
    {
      label: t(CHECKLIST_RESOURCES.Items.FacilityInfo),
      item: checklist.facilityInfo,
    },
    {
      label: t(CHECKLIST_RESOURCES.Items.BankAccountInfo),
      item: checklist.bankAccountInfo,
    },
    {
      label: t(CHECKLIST_RESOURCES.Items.ExecutiveInfo),
      item: checklist.executiveInfo,
    },
    {
      label: t(CHECKLIST_RESOURCES.Items.ExecutiveDocuments),
      item: checklist.executiveDocuments,
    },
    {
      label: t(CHECKLIST_RESOURCES.Items.TerminalAssignment),
      item: checklist.terminalAssignment,
    },
  ];

  return (
    <Layout>
      <List disablePadding dense>
        {items.map(({ label, item }) => (
          <ListItem key={label} disableGutters alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
              {item.isValid ? (
                <CheckCircleIcon color="success" fontSize="small" />
              ) : (
                <InfoOutlinedIcon color="warning" fontSize="small" />
              )}
            </ListItemIcon>
            {/* Support needs the same per-person detail the merchant sees — it is what they read out
                over the phone when the merchant asks what is still missing. */}
            <ListItemText
              primary={label}
              secondary={<ChecklistIssues item={item} />}
              slotProps={{
                secondary: { color: "text.secondary", component: "div" },
              }}
            />
          </ListItem>
        ))}
      </List>
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2">
        {t(RESOURCES.ChecklistSection)}
      </Typography>
      {children}
    </Stack>
  );
};
